import { useEffect, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";

import { userDetailSelect } from "Layout/slice/selector";
import { setUserDetails, toast } from "Layout/slice";
import { parseErrorMessage } from "utils";
import { Loader } from "components/Loader";
import {
  change2FA,
  sendStepUpCode,
  verifyStepUpCode,
} from "features/Account/service";
import { ChangePasswordModal } from "features/Account/v2/Security/ChangePassword/ChangePasswordModal";
import { IdentityVerification } from "features/Account/v2/Security/IdentityVerification";
import { PhoneVerify } from "../GeneralPanel/PhoneVerify";
import {
  PanelContainer,
  Toggle,
  DarkButton,
  OutlineButton,
} from "./PrivacyPanel.styles";

const RESEND_SECONDS = 20;

// Which switch the code box is confirming: "withdrawal" (turning withdrawal
// verification off) or "login" (turning login 2FA off while withdrawal
// verification is still on, which switches both off).
export const PrivacyPanel = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const user = useSelector(userDetailSelect);

  const [showPhoneSetup, setShowPhoneSetup] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [code, setCode] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [isBusy, setIsBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (seconds <= 0) return undefined;
    const id = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [seconds]);

  if (!user) return null;

  // Organization-level switch for MFA as a whole; the two flags below are the
  // user's own choices within it.
  const allowed = Boolean(user.mfa_allowed);
  const loginOn = Boolean(user.two_factor_auth_enabled);
  const withdrawalOn = Boolean(user.withdrawal_mfa_enabled);
  // Withdrawals only happen on the Surmount internal account, so without one
  // there is nothing for this to protect. An account that already has it on
  // can still switch it off.
  const hasInternalAccount = Boolean(user.is_surmount_internal_created);
  const withdrawalLocked = !withdrawalOn && (!hasInternalAccount || !loginOn);

  const notify = (body, type) =>
    dispatch(toast(type ? { type, body } : { body }));
  const notifyError = (error) => notify(parseErrorMessage(error), "error");

  // Read the user at dispatch time: a verified phone may have just been
  // written, and setUserDetails replaces the whole object.
  const patchUser = (patch) =>
    dispatch(
      setUserDetails({ ...userDetailSelect(store.getState()), ...patch })
    );

  const applyLogin = async (value) => {
    const response = await change2FA({ value: value ? "ON" : "OFF" });
    patchUser({
      two_factor_auth_enabled: response?.two_factor_auth_enabled ?? value,
    });
    notify(`2FA has been ${value ? "enabled" : "disabled"} successfully!`);
  };

  const applyWithdrawal = async (value, stepUpToken) => {
    const response = await change2FA({
      value: value ? "ON" : "OFF",
      scope: "withdrawal",
      ...(stepUpToken ? { step_up_token: stepUpToken } : {}),
    });
    patchUser({
      withdrawal_mfa_enabled: response?.withdrawal_mfa_enabled ?? value,
    });
    notify(
      `Withdrawal verification has been ${
        value ? "enabled" : "disabled"
      } successfully!`
    );
  };

  const requestCode = async () => {
    await sendStepUpCode({ action: "mfa_disable" });
    setSeconds(RESEND_SECONDS);
    notify("The code has been sent!", "info");
  };

  // Read the account at click time, not from the last render: a verified phone
  // or a switch may have changed a moment ago, and deciding on stale flags could
  // turn login 2FA off without the code while withdrawal verification stays on.
  const latest = () => userDetailSelect(store.getState()) || user;

  // One request at a time: a second click while one is running would send a
  // duplicate request or another SMS.
  const guarded = async (action) => {
    if (isBusy) return;
    setIsBusy(true);
    try {
      await action();
    } catch (error) {
      notifyError(error);
    } finally {
      setIsBusy(false);
    }
  };

  const handleLoginToggle = () =>
    guarded(async () => {
      const now = latest();
      if (!now.two_factor_auth_enabled) {
        // Turning on needs a verified phone: that is where the codes go.
        if (!now.phone_verified) {
          setShowPhoneSetup(true);
          return;
        }
        await applyLogin(true);
        return;
      }
      if (now.withdrawal_mfa_enabled) {
        // Turning login 2FA off also turns withdrawal verification off, which
        // is a protected action that needs a fresh code first.
        await requestCode();
        setConfirming("login");
        return;
      }
      await applyLogin(false);
    });

  const handleWithdrawalToggle = () =>
    guarded(async () => {
      if (!latest().withdrawal_mfa_enabled) {
        await applyWithdrawal(true);
        return;
      }
      await requestCode();
      setConfirming("withdrawal");
    });

  const handleConfirm = () =>
    guarded(async () => {
      let stepUpToken;
      try {
        ({ step_up_token: stepUpToken } = await verifyStepUpCode({
          action: "mfa_disable",
          mfa_code: code.trim(),
        }));
      } catch (error) {
        // A rejected code leaves the box open so it can be retyped.
        notifyError(error);
        return;
      }
      // The code is spent from here on, so close the box even if a switch
      // fails: trying again needs a new code, not the one on screen.
      const scope = confirming;
      setConfirming(null);
      setCode("");
      // Withdrawal first: if it fails, login 2FA stays on and the account is
      // never left with login off and withdrawal verification still on.
      await applyWithdrawal(false, stepUpToken);
      if (scope === "login") await applyLogin(false);
    });

  const handleCancelConfirm = () => {
    setConfirming(null);
    setCode("");
  };

  const handleResend = () => guarded(requestCode);

  const handlePhoneVerified = async () => {
    setShowPhoneSetup(false);
    try {
      await applyLogin(true);
    } catch (error) {
      notifyError(error);
    }
  };

  const maskedPhone = user.phone
    ? `••• ${String(user.phone).slice(-3)}`
    : "your phone";

  const confirmBox = (scope) =>
    confirming === scope && (
      <div className="box">
        <p className="box-text">
          {scope === "login"
            ? "Turning this off also turns off withdrawal verification. "
            : ""}
          To confirm, enter the code we sent to {maskedPhone}.
        </p>
        <form
          className="code-row"
          onSubmit={(e) => {
            e.preventDefault();
            handleConfirm();
          }}
        >
          <input
            type="text"
            autoFocus
            inputMode="numeric"
            maxLength={6}
            placeholder="Code"
            aria-label="Verification code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <DarkButton type="submit" disabled={!code.trim() || isBusy}>
            {isBusy ? <Loader className="on-btn" /> : "Confirm"}
          </DarkButton>
          <OutlineButton
            type="button"
            onClick={handleResend}
            disabled={seconds > 0 || isBusy}
          >
            {seconds > 0
              ? `Resend in ${String(seconds).padStart(2, "0")}`
              : "Resend"}
          </OutlineButton>
          <OutlineButton
            type="button"
            onClick={handleCancelConfirm}
            disabled={isBusy}
          >
            Cancel
          </OutlineButton>
        </form>
      </div>
    );

  return (
    <PanelContainer>
      {allowed && (
        <>
          <div className="setting-row first">
            <div className="row-main">
              <div className="row-text">
                <p className="label" id="privacy-login-label">
                  Two-factor authentication (2FA)
                </p>
                <p className="hint">
                  Keep your account secure by enabling 2FA via SMS.
                </p>
              </div>
              <Toggle
                type="button"
                role="switch"
                aria-labelledby="privacy-login-label"
                aria-checked={loginOn}
                $on={loginOn}
                disabled={isBusy}
                onClick={handleLoginToggle}
              />
            </div>
            {showPhoneSetup && (
              <div className="box">
                <p className="box-text">
                  Verify your phone number to enable SMS authentication.
                </p>
                <PhoneVerify onVerified={handlePhoneVerified} />
              </div>
            )}
            {confirmBox("login")}
          </div>

          <div className="setting-row">
            <div className="row-main">
              <div className="row-text">
                <p className="label" id="privacy-withdrawal-label">
                  Withdrawal verification
                </p>
                <p className="hint">
                  Require a code before money can leave your account, even if
                  someone is already signed in.
                </p>
                {withdrawalLocked && (
                  <p className="hint muted" id="privacy-withdrawal-hint">
                    {hasInternalAccount
                      ? "Turn on two-factor authentication first."
                      : "Available once you open a Surmount account."}
                  </p>
                )}
              </div>
              <Toggle
                type="button"
                role="switch"
                aria-labelledby="privacy-withdrawal-label"
                aria-checked={withdrawalOn}
                $on={withdrawalOn}
                aria-describedby={
                  withdrawalLocked ? "privacy-withdrawal-hint" : undefined
                }
                disabled={withdrawalLocked || isBusy}
                onClick={handleWithdrawalToggle}
              />
            </div>
            {confirmBox("withdrawal")}
          </div>
        </>
      )}

      <div className={`setting-row${allowed ? "" : " first"}`}>
        <div className="row-main">
          <div className="row-text">
            <p className="label">Password</p>
            <p className="hint">
              Set a unique password to protect your Surmount account.
            </p>
          </div>
          <OutlineButton type="button" onClick={() => setShowPassword(true)}>
            Change password
          </OutlineButton>
        </div>
      </div>

      <IdentityVerification hideWhenNothingToDo />

      <ChangePasswordModal
        show={showPassword}
        onHide={() => setShowPassword(false)}
      />
    </PanelContainer>
  );
};
